/** Render lint results (WS5 ports prod.lint): dict of check → value. Values with ok/pass booleans get a pass/fail.
 * `hard` / `soft` are lists of findings `{error_type, why}`: any hard finding fails, any soft finding warns, and each
 * finding is listed with its text. */
export type LintFinding = { error_type: string; why: string };
export type LintRow = { check: string; result: "pass" | "fail" | "warn" | "info"; detail: string; findings?: LintFinding[] };

/** Keys whose value is a list of findings, and the result a non-empty list gives. */
const FINDING_KEYS: Record<string, "fail" | "warn"> = { hard: "fail", soft: "warn" };

function findingsOf(v: unknown): LintFinding[] | null {
  if (!Array.isArray(v)) return null;
  return v.map((x) => {
    if (x && typeof x === "object") {
      const o = x as Record<string, unknown>;
      return { error_type: String(o.error_type ?? "other"), why: typeof o.why === "string" ? o.why : JSON.stringify(o) };
    }
    return { error_type: "other", why: String(x) };
  });
}

export function lintRows(lint: Record<string, unknown>): LintRow[] {
  return Object.entries(lint).map(([check, v]) => {
    const bad = FINDING_KEYS[check];
    const findings = bad ? findingsOf(v) : null;
    if (bad && findings) {
      return { check, result: findings.length ? bad : "pass", detail: findings.length ? "" : "none", findings };
    }
    let result: LintRow["result"] = "info";
    let rest: unknown = v;
    if (typeof v === "boolean") {
      result = v ? "pass" : "fail";
      rest = "";
    } else if (v && typeof v === "object" && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      const ok = typeof o.ok === "boolean" ? o.ok : typeof o.pass === "boolean" ? o.pass : typeof o.passed === "boolean" ? o.passed : null;
      if (ok !== null) result = ok ? "pass" : "fail";
      const { ok: _a, pass: _b, passed: _c, ...others } = o;
      rest = Object.keys(others).length ? others : "";
    }
    const detail = typeof rest === "string" ? rest : JSON.stringify(rest);
    return { check, result, detail };
  });
}

/** Failing and warning counts: each hard / soft finding counts once, any other failing row once. */
export function lintCounts(rows: LintRow[]): { failing: number; warnings: number } {
  let failing = 0;
  let warnings = 0;
  for (const r of rows) {
    const n = r.findings ? r.findings.length : 1;
    if (r.result === "fail") failing += n;
    else if (r.result === "warn") warnings += n;
  }
  return { failing, warnings };
}

const BADGE: Record<LintRow["result"], string> = { pass: "ws8-badge-ok", fail: "ws8-badge-bad", warn: "ws8-badge-warn", info: "" };

export function LintTable({ lint }: { lint: Record<string, unknown> }) {
  const rows = lintRows(lint);
  if (rows.length === 0) return <p className="ws8-muted" data-testid="lint-empty">No lint results.</p>;
  const { failing, warnings } = lintCounts(rows);
  return (
    <table className="ws8-table" data-testid="lint-table">
      <caption>Lint ({failing} failing, {warnings} warning{warnings === 1 ? "" : "s"})</caption>
      <thead><tr><th scope="col">Check</th><th scope="col">Result</th><th scope="col">Detail</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.check} data-testid={`lint-row-${r.check}`} data-result={r.result}>
            <td>{r.check}</td>
            <td><span className={`ws8-badge ${BADGE[r.result]}`}>{r.result === "warn" ? "warning" : r.result}</span></td>
            <td>
              {r.findings && r.findings.length > 0 ? (
                <ul className={r.result === "fail" ? "ws8-bad" : "ws8-warn"} aria-label={`${r.check} lint findings`}>
                  {r.findings.map((f, i) => <li key={i}><strong>{f.error_type}</strong>: {f.why}</li>)}
                </ul>
              ) : r.detail}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
